import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAssetMeta, holderLabel, tagText, deviceName, LIFECYCLE_LABEL, lifecycleTone } from '../lib/assets';
import { contractorAuth } from '../lib/contractorAuth';
import { useConfirm } from '../components/ConfirmDialog';
import { CategoryIcon, pinStatus } from '../components/MapPin';
import '../components/Assets.css';
import BackLink from '../components/asset/BackLink';
import { cameFrom } from '../components/asset/BackLink';

/**
 * ONE DESK (108): a place the screens show as a holder.
 *
 * Gordon, 28 October: "a workstation is no different than a user. It would
 * comprise of several assets such as monitors, docks, etc. A user can have
 * a fixed workstation or they could hot-desk." So the page reads like a
 * person's: who the desk is for (fixed, somebody's or unassigned; or hot),
 * its kit (the devices at it held by nobody), the people's own things that
 * happen to be at it, any of its kit seen at another desk for IT to
 * confirm, and - for IT administrators only - who sat at it over the last
 * 90 days, from their laptops' reports.
 *
 * "Clear desk" puts every piece of kit into a storage place by the
 * put-away rules; "Print its label" is the place-label page (its QR opens
 * this page); "Show on the map" opens the plan its pin is on.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const when = (t) => (t ? new Date(t).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

export default function AssetDesk() {
  const { id } = useParams();
  const navigate = useNavigate();
  const meta = useAssetMeta();
  const [confirm, confirmDialog] = useConfirm();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [problem, setProblem] = useState('');
  const [who, setWho] = useState('');
  const [name, setName] = useState('');
  const [storeId, setStoreId] = useState('');
  const [busy, setBusy] = useState(false);
  const [cleared, setCleared] = useState(null);

  const load = useCallback(() => axios.get(`${API_BASE}/assets/desks/${id}`, contractorAuth())
    .then((r) => {
      setData(r.data);
      setError(null);
      const d = r.data.desk;
      setWho(d.desk_mode === 'hot' ? 'hot' : d.desk_user_id ? `u:${d.desk_user_id}` : 'unassigned');
      setName(d.name);
    })
    .catch((e) => setError({ status: e.response?.status, text: e.response?.data?.error || 'Could not load this desk.' })), [id]);
  useEffect(() => { load(); }, [load]);

  const save = async (body) => {
    setProblem('');
    setBusy(true);
    try {
      await axios.put(`${API_BASE}/assets/desks/${id}`, body, contractorAuth());
      await load();
    } catch (e) {
      setProblem(e.response?.data?.error || 'That did not work.');
    } finally { setBusy(false); }
  };
  const saveWho = () => save(who === 'hot' ? { desk_mode: 'hot' }
    : who === 'unassigned' ? { desk_mode: 'fixed', user_id: null } : { user_id: who.slice(2) });

  const clearDesk = async () => {
    const store = (meta?.locations || []).find((l) => l.id === storeId);
    if (!(await confirm({ title: 'Clear this desk?', confirmLabel: 'Clear the desk',
      message: `Every piece of its kit goes to ${store?.path || 'the storage place'}, unplugged and back in stock. Things that are somebody’s stay here.` }))) return;
    setProblem('');
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/assets/desks/${id}/clear`, { storage_id: storeId }, contractorAuth());
      setCleared(r.data);
      await load();
    } catch (e) {
      setProblem(e.response?.data?.error || 'That did not work.');
    } finally { setBusy(false); }
  };

  const decide = async (flag, action) => {
    setProblem('');
    try {
      await axios.post(`${API_BASE}/assets/desks/${id}/conflicts/${flag.id}`, { action }, contractorAuth());
      await load();
    } catch (e) {
      setProblem(e.response?.data?.error || 'That did not work.');
    }
  };

  if (error) {
    return (
      <div className="container">
        <p><BackLink /></p>
        <p className={error.status === 503 ? 'as-note' : 'as-error'} role="alert">
          {error.status === 404 ? 'There is no such desk, or it is not one you may see.' : error.text}
        </p>
      </div>
    );
  }
  if (!data) return <div className="container"><p className="as-quiet">Loading…</p></div>;

  const d = data.desk;
  const stores = (meta?.locations || []).filter((l) => l.active && ['storage', 'container', 'shelf'].includes(l.kind));
  // Full paths are too long to read in a select, so the storage places are
  // grouped under their storage room and each is named from the room down
  // ("Cupboard A › Shelf 3"); the chosen place's full path is shown below.
  const storeById = new Map(stores.map((l) => [l.id, l]));
  const roomOf = (l) => {
    let at = l;
    for (let i = 0; i < 20 && at.kind !== 'storage' && storeById.has(at.parent_id); i += 1) at = storeById.get(at.parent_id);
    return at;
  };
  const storeGroups = [];
  for (const l of stores) {
    const room = roomOf(l);
    let g = storeGroups.find((x) => x.room.id === room.id);
    if (!g) { g = { room, places: [] }; storeGroups.push(g); }
    g.places.push(l);
  }
  const shortName = (l, room) => (l.id === room.id ? `${room.name} (the room itself)`
    : l.path && room.path && l.path.startsWith(`${room.path} › `) ? l.path.slice(room.path.length + 3) : l.name);
  const chosenStore = storeById.get(storeId);
  const row = (a) => (
    <li key={a.id}>
      <span className="as-pin-badge" data-tone={pinStatus(a).tone} title={pinStatus(a).label}><CategoryIcon category={a.category_name} /></span>
      <Link to={`/assets/${a.id}`} state={cameFrom(data?.desk?.label || 'Desk')}><strong>{tagText(a)}</strong></Link>
      <span className="as-quiet">{` ${a.category_name}${deviceName(a) !== a.category_name ? ` · ${deviceName(a)}` : ''}`}</span>
      {a.holder_name && <span className="as-quiet">{` · ${holderLabel(a)}’s`}</span>}
      <span className="as-chip" data-tone={lifecycleTone(a.lifecycle_status)}>{LIFECYCLE_LABEL[a.lifecycle_status]}</span>
    </li>
  );

  const whoUser = (meta?.users || []).find((u) => `u:${u.id}` === who);
  const whoLabel = who === 'hot' ? 'Hot desk — whoever sits there' : who === 'unassigned' ? 'Fixed, not given to anybody'
    : whoUser ? `${whoUser.display_name || whoUser.email}’s desk` : d.desk_user_left ? `${d.desk_user_name} (has left)` : '';

  return (
    <div className="container" data-testid="desk-page">
      <p><BackLink /></p>
      <div className="page-header">
        <h1 className="page-title">{d.label}</h1>
        <p className="page-description">{`Where: ${d.path}`}</p>
      </div>
      <div className="as-toolbar">
        <span className="as-chip" data-tone={d.desk_mode === 'hot' ? 'info' : d.desk_user_id ? 'success' : 'idle'} data-testid="desk-mode">
          {d.desk_mode === 'hot' ? 'Hot desk' : d.desk_user_id ? `Fixed — ${d.desk_user_name}` : 'Fixed — unassigned'}
        </span>
        {d.desk_user_left && <span className="as-chip" data-tone="warning">{`${d.desk_user_name} has left`}</span>}
        {data.today && <span className="as-chip" data-tone="info" data-testid="desk-today">{`Today: ${data.today}`}</span>}
        <span className="as-spacer" />
        {d.parent_id && <button type="button" className="btn btn-secondary" onClick={() => navigate(`/assets/map/${d.parent_id}`)}>Show on the map</button>}
        {data.write && !contractorAuth() && (
          <button type="button" className="btn btn-secondary" onClick={() => navigate(`/assets/places/${d.id}/labels`)}>Print its label</button>
        )}
      </div>
      {problem && <p className="as-error" role="alert">{problem}</p>}

      {data.write && (
        <section className="as-panel" aria-labelledby="desk-who">
          <h2 id="desk-who">Whose desk</h2>
          <div className="as-inline-form as-desk-who">
            <select aria-label="Who the desk is for" title={whoLabel} value={who} onChange={(e) => setWho(e.target.value)}>
              <option value="hot">Hot desk — whoever sits there</option>
              <option value="unassigned">Fixed, not given to anybody</option>
              {d.desk_user_left && <option value={`u:${d.desk_user_id}`} disabled>{`${d.desk_user_name} (has left)`}</option>}
              {(meta?.users || []).map((u) => <option key={u.id} value={`u:${u.id}`}>{`${u.display_name || u.email}’s desk`}</option>)}
            </select>
            <button type="button" className="btn btn-primary btn-small" disabled={busy} onClick={saveWho}>Save</button>
            {whoLabel.length > 26 && <p className="as-desk-who-full" data-testid="desk-who-full">{whoLabel}</p>}
            <span className="as-spacer" />
            <input type="text" aria-label="Desk name" className="as-desk-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
            <button type="button" className="btn btn-secondary btn-small" disabled={busy || !name.trim() || name.trim() === d.name}
              onClick={() => save({ name: name.trim() })}>Rename</button>
          </div>
          <p className="as-quiet">Nothing on the desk is handed over when it changes hands: its kit is the desk’s.</p>
        </section>
      )}

      {data.conflicts.length > 0 && (
        <section className="as-panel" aria-labelledby="desk-flags" data-testid="desk-conflicts">
          <h2 id="desk-flags">Seen at another desk</h2>
          <ul className="as-tree">
            {data.conflicts.map((k) => (
              <li key={k.id}>
                <span>{`One of its ${k.category_name.toLowerCase()}s (${k.asset_tag || deviceName(k)}) was seen at ${k.seen_desk_name}${k.laptop_tag ? `, plugged into ${k.laptop_tag}` : ''}, ${when(k.last_seen_at)}.`}</span>
                <span className="as-spacer" />
                {data.write && (
                  <>
                    <button type="button" className="btn btn-secondary btn-small" onClick={() => decide(k, 'move')}>{`It is at ${k.seen_desk_name} now`}</button>
                    <button type="button" className="btn btn-plain btn-small" onClick={() => decide(k, 'keep')}>It stays here</button>
                  </>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="as-panel" aria-labelledby="desk-kit" data-testid="desk-kit">
        <h2 id="desk-kit">{`Its kit (${data.kit.length})`}</h2>
        {data.kit.length ? <ul className="as-tree">{data.kit.map(row)}</ul> : <p className="as-quiet">Nothing is kept at this desk.</p>}
        {data.personal.length > 0 && (
          <>
            <h3>Somebody’s own, at this desk</h3>
            <ul className="as-tree">{data.personal.map(row)}</ul>
          </>
        )}
        {data.write && data.kit.length > 0 && (
          <div className="as-inline-form as-desk-store">
            <select aria-label="Put the kit away in" title={chosenStore?.path || undefined} value={storeId} onChange={(e) => setStoreId(e.target.value)}>
              <option value="">{stores.length ? 'Put it all away in…' : 'No storage places yet'}</option>
              {storeGroups.map((g) => (
                <optgroup key={g.room.id} label={g.room.path || g.room.name}>
                  {g.places.map((l) => <option key={l.id} value={l.id}>{shortName(l, g.room)}</option>)}
                </optgroup>
              ))}
            </select>
            <button type="button" className="btn btn-secondary btn-small" disabled={busy || !storeId} onClick={clearDesk}>Clear desk</button>
            {chosenStore && <p className="as-desk-who-full" data-testid="desk-store-full">{chosenStore.path || chosenStore.name}</p>}
          </div>
        )}
        {cleared && (
          <p className="as-note" data-tone="success" role="status">
            {`${cleared.done.length} put away${cleared.refused.length ? `; ${cleared.refused.length} could not be: ${cleared.refused.map((x) => x.reason).join('; ')}` : ''}.`}
          </p>
        )}
      </section>

      {Array.isArray(data.sittings) && (
        <section className="as-panel" aria-labelledby="desk-sittings" data-testid="desk-sittings">
          <h2 id="desk-sittings">Who sat here</h2>
          <p className="as-quiet">{`From laptops’ reports of this desk’s screens and dock. IT administrators only; kept ${data.keep_days} days.`}</p>
          {data.sittings.length ? (
            <ul className="as-tree">
              {data.sittings.map((s) => (
                <li key={s.id}>
                  <strong>{s.user_name || 'Nobody recorded'}</strong>
                  <span className="as-quiet">{` · ${s.laptop_tag || 'a laptop'} · ${when(s.first_seen_at)} – ${when(s.last_seen_at)}`}</span>
                  {s.via_category && <span className="as-quiet">{` · by its ${s.via_category.toLowerCase()}`}</span>}
                </li>
              ))}
            </ul>
          ) : <p className="as-quiet">Nobody has been seen at it.</p>}
        </section>
      )}
      {confirmDialog}
    </div>
  );
}
